// FloorCard — single diamond card in the stack.
//
// Visual: rotate(45deg) outer + rotate(-45deg) inner counter-rotate so
// content stays upright while the card border looks like an isometric
// rhombus. Hover: lift Y -8px + scale 1.04. Selected (expanding): kept
// in place while siblings drift apart.
//
// Status colours map to design-token variants — see floor-stack.md §5.

import React from "react";
import type { BuildingFloorDto, FloorStatusSnapshotDto, FloorSummaryStatus } from "../lib/floorStackApi.js";
import { TIMINGS } from "./state/floorStackMachine.js";

export type CardPhase = "idle" | "hover" | "dim" | "selected" | "expanded" | "collapsing";

interface Props {
  floor: BuildingFloorDto;
  snapshot: FloorStatusSnapshotDto | null;
  phase: CardPhase;
  reduced: boolean;
  onHover: () => void;
  onUnhover: () => void;
  onClick: () => void;
}

const STATUS_COLOURS: Record<FloorSummaryStatus, { fill: string; border: string; glow: string; ringHex: string }> = {
  idle:      { fill: "rgba(155,155,161,0.12)", border: "rgba(155,155,161,0.32)", glow: "rgba(155,155,161,0.18)",  ringHex: "#9b9ba1" },
  normal:    { fill: "rgba(0,217,126,0.12)",   border: "rgba(0,217,126,0.45)",   glow: "rgba(0,217,126,0.30)",     ringHex: "#00d97e" },
  attention: { fill: "rgba(255,184,0,0.14)",   border: "rgba(255,184,0,0.55)",   glow: "rgba(255,184,0,0.35)",     ringHex: "#ffb800" },
  critical:  { fill: "rgba(255,77,109,0.16)",  border: "rgba(255,77,109,0.65)",  glow: "rgba(255,77,109,0.45)",    ringHex: "#ff4d6d" },
  disabled:  { fill: "rgba(80,80,90,0.20)",    border: "rgba(110,110,120,0.45)", glow: "rgba(0,0,0,0.30)",          ringHex: "#6b6b73" },
};

export default function FloorCard({ floor, snapshot, phase, reduced, onHover, onUnhover, onClick }: Props): React.ReactElement {
  const summary = snapshot?.summary_status ?? floor.last_status_color ?? "idle";
  const c = STATUS_COLOURS[summary];
  const completionPct = snapshot && snapshot.location_total > 0
    ? Math.round(snapshot.completion_rate * 100)
    : null;

  // Outer rotate(45deg) gives the diamond. Inner rotate(-45deg) keeps text upright.
  // Phase translates to compound transform on the outer wrapper.
  const transform = (() => {
    if (reduced) return "none";
    switch (phase) {
      case "hover": return "translateY(-10px) scale(1.04)";
      case "dim": return "translateY(0px) scale(0.98)";
      case "selected": return "translateY(-14px) scale(1.06)";
      case "expanded": return "translateY(0px) scale(1.0)";
      case "collapsing": return "translateY(-6px) scale(1.02)";
      default: return "translateY(0px) scale(1.0)";
    }
  })();
  const opacity = phase === "dim" ? 0.55 : 1;
  const boxShadow = `0 18px 32px -16px ${c.glow}, 0 0 0 1px ${c.border}`;

  return (
    <button
      type="button"
      data-floor-id={floor.id}
      data-floor-status={summary}
      data-floor-phase={phase}
      onMouseEnter={onHover}
      onMouseLeave={onUnhover}
      onFocus={onHover}
      onBlur={onUnhover}
      onClick={onClick}
      disabled={summary === "disabled"}
      aria-label={`${floor.floor_name} 樓層 — 狀態 ${summary}`}
      className="floor-card relative inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-md"
      style={{
        transform,
        opacity,
        transition: reduced
          ? undefined
          : `transform ${phase === "hover" || phase === "selected" ? TIMINGS.hoverInMs : TIMINGS.hoverOutMs}ms cubic-bezier(0.22,1,0.36,1), opacity ${TIMINGS.hoverInMs}ms ease-out`,
        cursor: summary === "disabled" ? "not-allowed" : "pointer",
      }}
    >
      <div
        className="floor-card-rhombus relative"
        style={{
          width: "180px",
          height: "180px",
          transform: reduced ? "rotate(0deg)" : "rotate(45deg)",
          background: c.fill,
          boxShadow,
          borderRadius: "8px",
          border: `1px solid ${c.border}`,
        }}
      >
        {/* Inner content rotated back to upright */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center"
          style={{ transform: reduced ? "rotate(0deg)" : "rotate(-45deg)" }}
        >
          <span className="text-3xl font-mono font-semibold tracking-tight text-text-primary">{floor.floor_name}</span>
          {floor.is_critical === 1 && (
            <span className="mt-0.5 inline-flex h-1.5 w-1.5 rounded-full" style={{ background: c.ringHex }} />
          )}
          {snapshot ? (
            <div className="mt-1 flex flex-col items-center text-xs">
              <span className="text-text-secondary">
                {snapshot.completed}/{snapshot.location_total}
              </span>
              {completionPct !== null && (
                <span className="font-mono tabular-nums text-text-muted">{completionPct}%</span>
              )}
              {(snapshot.incident_warning_count + snapshot.incident_critical_count) > 0 && (
                <span className="mt-0.5 inline-flex items-center gap-0.5 text-[10px]" style={{ color: c.ringHex }}>
                  ⚠ {snapshot.incident_warning_count + snapshot.incident_critical_count}
                </span>
              )}
            </div>
          ) : (
            <span className="mt-1 text-[10px] text-text-muted">—</span>
          )}
        </div>
      </div>
    </button>
  );
}
